import Box from '@mui/material/Box';
import {uuid} from '../../../../utils/utils';
import Paper from '@mui/material/Paper';
import React, {ReactElement, useContext, useState} from 'react';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import {useWorkspaceContext} from '../../../../context/workspaceContext';
import {FormControl, MenuItem, Select, TextField} from '@mui/material';
import {DateValidationError, DesktopDatePicker, LocalizationProvider} from '@mui/x-date-pickers';
import {hasRightToEdit} from '../../../../utils/role-to-rights';
import {ResourceType} from '../../../../@types/resource-type';
import Group from '../../../../@types/group';
import {fetchAllResources, getResourceNameWithAscendants} from '../../../../utils/resource-utils';
import moment, {Moment} from 'moment';
import {AdapterMoment} from '@mui/x-date-pickers/AdapterMoment';
import {OccurrenceEditorProps} from '../../../../@types/editor-props';
import InputLabel from '@mui/material/InputLabel';
import Typography from '@mui/material/Typography';
import TableHead from '@mui/material/TableHead';
import DeleteIcon from '@mui/icons-material/Delete';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import Occurrence from '../../../../@types/occurrence';
import OccurrenceService from '../../../../services/occurrence.service';
import ErrorAlertComponent from '../../../ErrorAlert.component';
import {ResourceAction} from '../../../../@types/resource-action';
import {UserContext} from '../../../../context/userContext';
import {toast} from 'react-toastify';

interface EntryProp {
    text: string|null;
    error: boolean;
    errorText?: string;
}

const defaultEntries = [
    { key: { text: 'name', error: false }, value: { text: '', error: false } },
    { key: { text: 'description', error: false }, value: { text: '', error: false }}];

const maxTextLength = 255;

const requiredKeys = ['name', 'description'];

export default function OccurrenceReportEditor(props: OccurrenceEditorProps): ReactElement {
    const { occurrence, isActive, updateTab } = props;

    const { user } = useContext(UserContext);
    const [uniqueId, setUniqueId] = React.useState<string>('');
    const [occurrenceDate, setOccurrenceDate] = React.useState<Moment|null>(moment());
    const [occurrenceSource, setOccurrenceSource] = React.useState<string>('');
    const [occurrenceType, setOccurrenceType] = React.useState<string>('UNASSIGNED');
    const [groupId, setGroupId] = React.useState<number>(0);
    const [locationName, setLocationName] = React.useState<string>('');
    const [latitude, setLatitude] = React.useState<string>('');
    const [longitude, setLongitude] = React.useState<string>('');
    const [altitude, setAltitude] = React.useState<string>('');
    const [customEntries, setCustomEntries] = React.useState<{key: EntryProp, value: EntryProp}[]>(defaultEntries);
    const [groups, setGroups] = React.useState<Group[]>([]);
    const [uniqueIdError, setUniqueIdError] = React.useState(false);
    const [occurrenceDateError, setOccurrenceDateError] = React.useState<DateValidationError | null>(null);
    const [latitudeError, setLatitudeError] = React.useState(false);
    const [longitudeError, setLongitudeError] = React.useState(false);
    const [altitudeError, setAltitudeError] = React.useState(false);
    const [notSavable, setNotSavable] = React.useState<boolean>(false);
    const [error, setError] = useState<string|null>(null);
    const uuidRef = React.useRef(uuid());
    const { setCurrentWorkspaceObject, workspaceObjects, setWorkspaceObjects, targetGroup, occurrenceTypes, getAdditionalResource, addOrUpdateAdditionalResource, currentAction, setCurrentAction } = useWorkspaceContext();


    React.useEffect(() => {
        if (isActive && currentAction === ResourceAction.SAVE) {
            setCurrentAction(null);
            handleSave();
        }
    }, [currentAction])

    React.useEffect(() => {
        let existingGroups = getAdditionalResource(ResourceType.GROUPS).resources;
        if (!existingGroups) {
            fetchAllResources(ResourceType.GROUPS)?.then((grps) => {
                grps.sort((a, b) => a.name.localeCompare(b.name))
                setGroups(grps);
                setGroupId(occurrence?.group.id ?? targetGroup ?? ((grps && grps[0]) ? grps[0].id! : 0));
                addOrUpdateAdditionalResource({resources: grps, objectType: ResourceType.GROUPS});
            })
        } else {
            existingGroups.sort((a, b) => a.name.localeCompare(b.name))
            setGroups(existingGroups);
            setGroupId(occurrence?.group.id ?? targetGroup ?? ((existingGroups && existingGroups[0]) ? existingGroups[0].id! : 0));
        }
    }, [])

    React.useEffect(() => {
        if (occurrence) {
            if (occurrence.group.id) {
                setGroupId(occurrence.group.id);
            } else {
                setGroupId(targetGroup ?? ((groups && groups[0]) ? groups[0].id! : 0));
            }
            setUniqueId(occurrence.uniqueId);
            setOccurrenceDate(moment(occurrence.dateTime));
            setOccurrenceType(occurrence.occurrenceType ?? '0');
            setOccurrenceSource(occurrence.occurrenceSource ?? '');
            if (occurrence.location) {
                setLocationName(occurrence.location.name);
                setLatitude(occurrence.location.latitude ? occurrence.location.latitude.toString() : '');
                setLongitude(occurrence.location.longitude ? occurrence.location.longitude.toString() : '');
                setAltitude(occurrence.location.altitude ? occurrence.location.altitude.toString() : '');
            }
            if (occurrence.rawReport) {
                let newEntries: { key: EntryProp, value: EntryProp }[] = [];
                for (var entry in occurrence.rawReport)
                    newEntries.push({
                        key: {text: entry, error: false},
                        value: {text: occurrence.rawReport[entry], error: false}
                    });
                setCustomEntries(newEntries);
            } else {
                setCustomEntries(defaultEntries);
            }
        }
    }, [occurrence])

    React.useEffect(() => {
        updateSavable();
    },[uniqueId, occurrenceDate, groupId, occurrenceType, latitude, longitude, altitude, customEntries]);

    const handleUniqueIdChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        if (event.target.value.length <= maxTextLength) {
            setUniqueId(event.target.value);
        }
        if (event.target.value.trim() === '') {
            setUniqueIdError(true);
        } else {
            setUniqueIdError(false);
        }
    };

    const occurrenceDateErrorMessage = React.useMemo(() => {
        switch (occurrenceDateError) {
            case 'invalidDate': {
                return 'Occurrence date is not valid';
            }

            case 'disableFuture': {
                return 'Occurrence date cannot be in future';
            }

            default: {
                return '';
            }
        }
    }, [occurrenceDateError]);

    const handleLocationNameChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        if (event.target.value.trim().length <= maxTextLength) {
            setLocationName(event.target.value);
        }
    };

    const handleOccurrenceSourceChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setOccurrenceSource(event.target.value);
    };

    const handleOccurrenceTypeChange = (event: any): void => {
        setOccurrenceType(event.target.value);
    };

    const handleGroupChange = (event: any): void => {
        setGroupId(event.target.value as number);
    };

    const handleLatitudeChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setLatitude(event.target.value.trim());
        validateLatitude(event.target.value.trim(), true);
    }

    const handleLatitudeOnBlur = (event: React.FocusEvent<HTMLInputElement>): void => {
        setLatitude(event.target.value.trim());
        validateLatitude(event.target.value.trim(), false);
    }

    const validateLatitude = (latStr: string, allowSigns: boolean): void => {
        if (latStr === '' || (allowSigns && (latStr === '-' || latStr === '+'))) {
            setLatitudeError(false);
        } else {
            let latValue = +latStr;
            setLatitudeError(!latValue || latValue > 90.0 || latValue < -90.0);
        }
    };

    const handleLongitudeChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setLongitude(event.target.value.trim());
        validateLongitude(event.target.value.trim(), true);
    }

    const handleLongitudeOnBlur = (event: React.FocusEvent<HTMLInputElement>): void => {
        setLongitude(event.target.value.trim());
        validateLongitude(event.target.value.trim(), false);
    }

    const validateLongitude = (lonStr: string, allowSigns: boolean): void => {
        if (lonStr === '' || (allowSigns && (lonStr === '-' || lonStr === '+'))) {
            setLongitudeError(false);
        } else {
            let longValue = +lonStr;
            setLongitudeError(!longValue || longValue > 180.0 || longValue < -180.0);
        }
    };

    const handleAltitudeChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setAltitude(event.target.value.trim());
        validateAltitude(event.target.value.trim(), true);
    }

    const handleAltitudeOnBlur = (event: React.FocusEvent<HTMLInputElement>): void => {
        setAltitude(event.target.value.trim());
        validateAltitude(event.target.value.trim(), false);
    }

    const validateAltitude = (altStr: string, allowSigns: boolean): void => {
        if (altStr === '' || (allowSigns && altStr === '+')) {
            setAltitudeError(false);
        } else {
            let altValue = +altStr;
            setAltitudeError(!altValue || altValue < 0);
        }
    };

    const updateEntryKey = (event: React.ChangeEvent<HTMLTextAreaElement>, index: number): void => {
        let key = event.target.value;
        if (key.trim() === '') {
            customEntries[index].key = {text: '', error: true, errorText: 'key cannot be empty'};
        } else if (customEntries.findIndex((entry) => entry.key.text === key) >= 0) {
            customEntries[index].key = {text: key, error: true, errorText: 'key already exists'};
        } else {
            customEntries[index].key = {text: key, error: false};
        }
        refreshEntries();
    };

    const updateEntryValue = (event: React.ChangeEvent<HTMLTextAreaElement>, index: number): void => {
        customEntries[index].value = { text: event.target.value, error: (requiredKeys.includes(customEntries[index].key.text!) && event.target.value.trim() === '') };
        refreshEntries();
    };

    const isNotEditable = (): boolean => {
        return occurrence.id !== null && occurrence.id !== undefined && !hasRightToEdit({objectType: ResourceType.OCCURRENCE_REPORTS, object: occurrence}, user?.superUser);
    }
    
    const removeEntry = (index: number): void => {
        customEntries.splice(index, 1);
        refreshEntries();
    }

    const handleAddNewPair = (): void => {
        if (customEntries.findIndex(entry => entry.key.text === '') < 0) {
            customEntries.push({key: {text: '', error: false}, value: { text: '', error: false }});
        }
        refreshEntries();
    }

    const refreshEntries = (): void => {
        let newEntries: {key: EntryProp, value: EntryProp}[] = [];
        customEntries.forEach(entry => {
            newEntries.push(entry);
        })
        setCustomEntries(newEntries);
    }

    const handleSave = (): void => {
        if (occurrence.id) {
            OccurrenceService.putOccurrence(formOccurrence())
                .then((occurrence: Occurrence) => {
                    updateOccurrence(occurrence);
                    toast.success('Occurrence Report ' + occurrence.uniqueId + ' has been updated successfully.');
                })
                .catch((err: Error) => {
                    setError(err.message);
                });
        } else {
            OccurrenceService.createOccurrence(formOccurrence())
                .then((occurrence: Occurrence) => {
                    updateOccurrence(occurrence);
                    toast.success('Occurrence Report ' + occurrence.uniqueId + ' has been created successfully.');
                })
                .catch((err: Error) => {
                    setError(err.message);
                });
        }
    }

    const updateOccurrence = (occurrence: Occurrence): void => {
        occurrence.group = getAdditionalResource(ResourceType.GROUPS).resources?.find((grp) => grp.id === occurrence.group.id);
        let occurrenceObject = {objectType: ResourceType.OCCURRENCE_REPORTS, object: occurrence};
        setCurrentWorkspaceObject(occurrenceObject);
        if (workspaceObjects?.objectType === ResourceType.OCCURRENCE_REPORTS) {
            setWorkspaceObjects({objectType: ResourceType.OCCURRENCE_REPORTS});
        }
        updateTab(occurrenceObject);
    }

    const formOccurrence = (): Occurrence => {
        const occurrenceData = {} as Occurrence;
        occurrenceData.uniqueId = uniqueId;
        occurrenceData.dateTime = occurrenceDate!.toDate();
        occurrenceData.id = occurrence.id;
        occurrenceData.group = getAdditionalResource(ResourceType.GROUPS)!.resources!.find((group) => group.id === groupId);
        occurrenceData.occurrenceSource = occurrenceSource === '' ? 'USER_DEFINED' : occurrenceSource;
        occurrenceData.occurrenceType = occurrenceType === '0' ? null : occurrenceType;
        if (occurrence) {
            occurrenceData.created = occurrence.created;
        }
        occurrenceData.location = {
            id: occurrence?.location?.id,
            name: locationName,
            latitude: latitude === '' ? null : +latitude,
            longitude: longitude === '' ? null : +longitude,
            altitude: altitude === '' ? null : +altitude,
        };
        let report: any = {};
        customEntries.forEach((entry) => {
            report[entry.key.text!] = entry.value.text!;
        });
        occurrenceData.rawReport = report;
        return occurrenceData;
    }

    const updateSavable = (): void => {
        let unsavable = isNotEditable() || uniqueId === '' || uniqueIdError || occurrenceDateError !== null || groupId === 0 || latitudeError || longitudeError || altitudeError;
        customEntries.forEach((entry) => {
            unsavable = unsavable || entry.key.error || entry.value.error ||
                (requiredKeys.includes(entry.key.text!) && (entry.value.text === null || entry.value.text === ''));
        });
        setNotSavable(unsavable);
    };

    if (groups && groups[0] && (groupId == 0 || isNaN(groupId))) { setGroupId(groups[0].id!); }

    return <Box sx={{height: '100%', p: 1}}>
        <Box sx={{ width: '100%' }}>
            <Stack spacing={2} direction="column" justifyContent='space-evenly'>
                <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 650 }} aria-label="simple table">
                        <TableBody>
                            <TableRow key={0}>
                                <TableCell component="th" scope="row">
                                    <TextField
                                        label='Unique ID'
                                        aria-label='unique id'
                                        type='text'
                                        variant="outlined"
                                        size='small'
                                        id={`unique-id-input-${uuidRef.current}`}
                                        autoComplete='on'
                                        value={uniqueId|| ''}
                                        onChange={handleUniqueIdChange}
                                        sx={{
                                            '& input': {
                                                border: 'none',
                                            }
                                        }}
                                        slotProps={{
                                            input: {
                                                'role': 'textbox',
                                                'tabIndex': 0,
                                                'aria-labelledby': `unique-id-input-${uuidRef.current}`,
                                                'aria-label': 'unique id'
                                            }
                                        }}
                                        disabled={isNotEditable()}
                                        required
                                        fullWidth
                                        helperText={uniqueIdError ? 'Unique ID is required' : ''}
                                        error={uniqueIdError}
                                    />
                                </TableCell>
                                <TableCell>
                                    <LocalizationProvider dateAdapter={AdapterMoment}>
                                        <DesktopDatePicker
                                            label='Occurrence Date'
                                            aria-label='occurrence date'
                                            format='MM/DD/YYYY'
                                            value={occurrenceDate}
                                            onChange={setOccurrenceDate}
                                            disabled={isNotEditable()}
                                            disableFuture
                                            onError={(newError: DateValidationError): void => { setOccurrenceDateError(newError); updateSavable(); }}
                                            slotProps={{
                                                field: {
                                                    clearable: false,
                                                    onBlur: () => setOccurrenceDate(occurrenceDate ?? moment()),
                                                },
                                                textField: {
                                                    fullWidth: true,
                                                    size: 'small',
                                                    required: true,
                                                    helperText: occurrenceDateErrorMessage,
                                                },
                                            }}
                                        />
                                    </LocalizationProvider>
                                </TableCell>
                                <TableCell>
                                    <FormControl fullWidth>
                                        <InputLabel tabIndex={-1}  id={`select-label-${uuidRef.current}`} htmlFor={`select-list-${uuidRef.current}`} size={'small'}>Select Working Area</InputLabel>
                                        <Select
                                            role='presentation'
                                            labelId={`select-label-${uuidRef.current}`}
                                            id={`select-${uuidRef.current}`}
                                            value={groupId}
                                            label='Select Working Area*'
                                            size='small'
                                            fullWidth={true}
                                            onChange={handleGroupChange}
                                            inputProps={
                                                {
                                                    'role': 'textbox',
                                                    'tabIndex': 0,
                                                    'id': `select-list-${uuidRef.current}`,
                                                }
                                            }
                                            disabled={occurrence.id !== undefined}
                                        >
                                            {occurrence.id ?
                                                <MenuItem key={occurrence.group.id} value={occurrence.group.id} dense>{getResourceNameWithAscendants(occurrence.group, ResourceType.GROUPS)}</MenuItem>
                                                :
                                                (isNotEditable() ? groups.map((group) => <MenuItem key={group.id} value={group.id} dense>{getResourceNameWithAscendants(group, ResourceType.GROUPS)}</MenuItem>) :
                                                    groups.filter(group => group.canAddOrEditResources()).map((group) => <MenuItem key={group.id} value={group.id} dense>{getResourceNameWithAscendants(group, ResourceType.GROUPS)}</MenuItem>))}
                                        </Select>
                                    </FormControl>
                                </TableCell>
                            </TableRow>
                            <TableRow key={1}>
                                <TableCell component="th" scope="row">
                                    <TextField
                                        label='Occurrence Report Source'
                                        aria-label='occurrence report source'
                                        type='text'
                                        variant="outlined"
                                        size='small'
                                        id={`occurrence-source-input-${uuidRef.current}`}
                                        autoComplete='on'
                                        value={occurrenceSource ? occurrenceSource : 'USER_DEFINED'}
                                        onChange={handleOccurrenceSourceChange}
                                        sx={{
                                            '& input': {
                                                border: 'none',
                                            }
                                        }}
                                        slotProps={{
                                            input: {
                                                'role': 'textbox',
                                                'tabIndex': 0,
                                                'aria-labelledby': `occurrence-source-input-${uuidRef.current}`,
                                                'aria-label': 'occurrence report source'
                                            }
                                        }}
                                        disabled={true}
                                        fullWidth
                                    />
                                </TableCell>
                                <TableCell>
                                    <FormControl fullWidth>
                                        <InputLabel tabIndex={-2}  id={`select-ot-label-${uuidRef.current}`} htmlFor={`select-ot-list-${uuidRef.current}`} size={'small'}>Occurrence Type</InputLabel>
                                        <Select
                                            role='presentation'
                                            labelId={`select-ot-label-${uuidRef.current}`}
                                            id={`select-ot-${uuidRef.current}`}
                                            label='Occurrence Type'
                                            value={occurrenceType}
                                            size={'small'}
                                            onChange={handleOccurrenceTypeChange}
                                            sx={{
                                                '& input': {
                                                    border: 'none',
                                                }
                                            }}
                                            inputProps={{
                                                'role': 'textbox',
                                                'tabIndex': 0,
                                                'aria-labelledby': `occurrence-type-input-${uuidRef.current}`,
                                                'aria-label': 'occurrence report type'
                                            }}
                                            disabled={isNotEditable()}
                                            fullWidth
                                        >
                                            <MenuItem value={0} dense> ----- None ----- </MenuItem>
                                            {occurrenceTypes?.map((type: string) => (
                                                <MenuItem key={type} value={type} dense>{type}</MenuItem>
                                            ))
                                            }
                                        </Select>
                                    </FormControl>
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        label='Location Name'
                                        aria-label='location name'
                                        type='text'
                                        variant="outlined"
                                        size='small'
                                        id={`location-name-input-${uuidRef.current}`}
                                        autoComplete='on'
                                        value={locationName|| ''}
                                        onChange={handleLocationNameChange}
                                        sx={{
                                            '& input': {
                                                border: 'none',
                                            }
                                        }}
                                        disabled={isNotEditable()}
                                        fullWidth
                                    />
                                </TableCell>
                            </TableRow>
                            <TableRow key={2}>
                                <TableCell component="th" scope="row">
                                    <TextField
                                        label='Latitude'
                                        aria-label='latitude'
                                        type='text'
                                        variant="outlined"
                                        size='small'
                                        id={`latitude-input-${uuidRef.current}`}
                                        autoComplete='on'
                                        value={latitude|| ''}
                                        onChange={handleLatitudeChange}
                                        sx={{
                                            '& input': {
                                                border: 'none',
                                            }
                                        }}
                                        slotProps={{
                                            input: {
                                                'role': 'textbox',
                                                'tabIndex': 0,
                                                'aria-labelledby': `latitude-input-${uuidRef.current}`,
                                                'aria-label': 'latitude',
                                                'type': 'numeric',
                                                'onBlur': handleLatitudeOnBlur,
                                            }
                                        }}
                                        disabled={isNotEditable()}
                                        error={latitudeError}
                                        helperText={latitudeError ? 'latitude must be between -90 and 90 or blank' : ''}
                                        fullWidth
                                    />
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        label='Longitude'
                                        aria-label='longitude'
                                        type='text'
                                        variant="outlined"
                                        size='small'
                                        id={`longitude-input-${uuidRef.current}`}
                                        autoComplete='on'
                                        value={longitude|| ''}
                                        onChange={handleLongitudeChange}
                                        sx={{
                                            '& input': {
                                                border: 'none',
                                            }
                                        }}
                                        slotProps={{
                                            input: {
                                                'role': 'textbox',
                                                'tabIndex': 0,
                                                'aria-labelledby': `longitude-input-${uuidRef.current}`,
                                                'aria-label': 'longitude',
                                                'type': 'numeric',
                                                'onBlur': handleLongitudeOnBlur,
                                            }
                                        }}
                                        disabled={isNotEditable()}
                                        error={longitudeError}
                                        helperText={longitudeError ? 'longitude must be between -180.0 and 180.0 or blank' : ''}
                                        fullWidth
                                    />
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        label='Altitude'
                                        aria-label='altitude'
                                        type='text'
                                        variant="outlined"
                                        size='small'
                                        id={`altitude-input-${uuidRef.current}`}
                                        autoComplete='on'
                                        value={altitude|| ''}
                                        onChange={handleAltitudeChange}
                                        sx={{
                                            '& input': {
                                                border: 'none',
                                            }
                                        }}
                                        slotProps={{
                                            input: {
                                                'role': 'textbox',
                                                'tabIndex': 0,
                                                'aria-labelledby': `altitude-input-${uuidRef.current}`,
                                                'aria-label': 'altitude',
                                                'endAdornment': 'ft',
                                                'type': 'numeric',
                                                'onBlur': handleAltitudeOnBlur
                                            }
                                        }}
                                        disabled={isNotEditable()}
                                        error={altitudeError}
                                        helperText={altitudeError ? 'altitude cannot be a negative number' : ''}
                                        fullWidth
                                    />
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
                <div style={{ marginLeft: '5px', marginRight: '10px' }}>
                    <Typography variant="h6" component="div">
                        Report
                    </Typography>
                    <Typography component="div">
                        The Occurrence Report is defined as an unlimited list of key-value pairs. To add new fields, click the add button below the table. Add as many fields are are necessary to capture the scope of the event.
                    </Typography>
                </div>
                <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 650 }} aria-label="simple table">
                        <TableHead>
                            <TableRow>
                                <TableCell align="left">Key</TableCell>
                                <TableCell align="left">Value</TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        {customEntries.map((entry: any, index: number) => <TableRow>
                            <TableCell>
                                <TextField
                                    aria-label={`${entry.key.text}-label`}
                                    type='text'
                                    variant="outlined"
                                    size='small'
                                    id={`${entry.key.text}-label-input-${uuidRef.current}`}
                                    value={`${entry.key.text}`}
                                    onChange={(event: React.ChangeEvent<HTMLTextAreaElement>): void => {
                                        updateEntryKey(event, index);
                                    }}
                                    sx={{
                                        '& input': {
                                            border: 'none',
                                        }
                                    }}
                                    disabled={requiredKeys.includes(entry.key.text!) || isNotEditable()}
                                    error={entry.key.error}
                                    helperText={entry.key.errorText ?? ''}
                                    fullWidth
                                />
                            </TableCell>
                            <TableCell>
                                <TextField
                                    aria-label={`${entry.key.text} value`}
                                    type='text'
                                    variant="outlined"
                                    size='small'
                                    id={`${entry.key.text}-value-input-${uuidRef.current}`}
                                    value={`${entry.value.text}`}
                                    onChange={(event: React.ChangeEvent<HTMLTextAreaElement>): void => {
                                        updateEntryValue(event, index);
                                    }}
                                    sx={{
                                        '& input': {
                                            border: 'none',
                                        }
                                    }}
                                    slotProps={{
                                        input: {
                                            'role': 'textbox',
                                            'tabIndex': 0,
                                            'aria-labelledby': `${entry.key.text}-input-${uuidRef.current}`,
                                            'aria-label': `${entry.key.text}`
                                        }
                                    }}
                                    disabled={isNotEditable()}
                                    error={entry.value.error}
                                    helperText={entry.value.error ? `${entry.key.text} value cannot be blank` : ''}
                                    fullWidth
                                />
                            </TableCell>
                            <TableCell>
                                {requiredKeys.includes(entry.key.text!) ? ''
                                    : <Button variant="contained" color="error"
                                        onClick={(): void => {
                                            removeEntry(index);
                                        }}
                                        disabled={isNotEditable()}
                                        startIcon={<DeleteIcon />}
                                        title='Remove Field'
                                        aria-label="Remove Field"></Button>}
                            </TableCell>
                        </TableRow>)}
                    </Table>
                </TableContainer>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 0.5, pr: 0 }}>
                    <Button
                        variant='contained'
                        title='Add New Key Value Pair'
                        component='button'
                        style={{ textTransform: 'none' }}
                        onClick={handleAddNewPair}
                        startIcon={<AddIcon />}
                        disabled={isNotEditable() || customEntries.findIndex((entry) => entry.key.text === '') >= 0}
                    >Add New Key Value Pair</Button>
                    <Button
                        variant='contained'
                        title='Save Occurrence Report'
                        component='button'
                        onClick={handleSave}
                        startIcon={<SaveAltIcon />}
                        disabled={notSavable}
                    >Save</Button>
                </Box>
                {error ? <ErrorAlertComponent message={error} />  : null}
            </Stack>
        </Box>
    </Box>;
}